import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errText, fetcher } from "@/lib/api";
import { SignalsTable } from "@/components/trading/SignalsTable";
import { SignalDetailModal } from "@/components/trading/SignalDetailModal";
import { NewsPulse } from "@/components/trading/NewsPulse";

const FILTERS = [["", "All"], ["pending_approval,recommended", "Awaiting approval"], ["executed", "Executed"], ["rejected_risk", "Rejected by risk"], ["hold", "Hold"]];

export default function Signals() {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: signals, mutate } = useSWR(`/signals?limit=150${filter ? `&status=${filter}` : ""}`, fetcher, { refreshInterval: 15000 });
  const { data: status } = useSWR("/system/status", fetcher);

  const analyzeAll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/analyze"); toast.success(`Analyzed ${data.length} markets`); mutate(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const counts = (signals || []).reduce((a, s) => ({ ...a, [s.status]: (a[s.status] || 0) + 1 }), {});

  return (
    <div className="space-y-3 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-xl font-bold text-white">AI Signals</h1>
          <p className="text-xs text-[#848e9c] mt-0.5">Every signal passes: market data → validation → indicators → regime → strategies → LLM interpretation → risk engine. Approval mode: <span className="text-[#fcd535] mono">{status?.approval_mode}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input w-auto h-8 py-0" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="ai-signals-filter-select">{FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <button onClick={analyzeAll} disabled={busy} className="btn-yellow rounded px-3 h-8 text-xs flex items-center gap-1.5" data-testid="analyze-all-button"><Sparkles size={13} />{busy ? "Analyzing…" : "Analyze all markets"}</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[["executed", "Executed"], ["pending_approval", "Pending"], ["recommended", "Recommended"], ["rejected_risk", "Risk rejected"], ["hold", "Hold"]].map(([k, l]) => (
          <div key={k} className="panel px-3 py-2"><div className="panel-title">{l}</div><div className="mono text-lg text-white">{counts[k] || 0}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-2">
        <div className="xl:col-span-8 panel p-1" data-testid="ai-signals-list"><SignalsTable signals={signals || []} onChange={mutate} onSelect={setSelected} /></div>
        <div className="xl:col-span-4"><NewsPulse /></div>
      </div>
      <SignalDetailModal signal={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
